import { Plus, Trash2, ChevronDown } from 'lucide-react'

/**
 * Renders an editable form inferred entirely from the *shape* of the value
 * it's given — no schema. Strings become inputs (or textareas for long
 * text), numbers become number inputs, booleans become checkboxes, arrays
 * become repeatable cards, and objects become labeled, collapsible groups.
 *
 * This exists because the 14 website pages each have a different nested
 * content shape, and writing a bespoke form per page isn't worth it when
 * every save already has to replace the whole object wholesale anyway.
 * Keeping edits structure-preserving here (arrays always stay arrays,
 * objects always keep their keys) is what makes that whole-object-replace
 * safe — see the Array.isArray() guards on the website's own render side.
 */

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary focus:border-primary'

function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function isLongString(v: string): boolean {
  return v.length > 70 || v.includes('\n')
}

/** Deep clone used as the template for a new array item. */
function cloneAsTemplate(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneAsTemplate)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = typeof v === 'string' ? '' : typeof v === 'number' ? 0 : cloneAsTemplate(v)
    }
    return out
  }
  if (typeof value === 'string') return ''
  if (typeof value === 'number') return 0
  if (typeof value === 'boolean') return false
  return value
}

export function JsonForm({
  value,
  onChange,
  depth = 0,
  label,
}: {
  value: unknown
  onChange: (next: unknown) => void
  depth?: number
  label?: string
}) {
  if (typeof value === 'string') {
    return isLongString(value) ? (
      <textarea
        className={inputClass}
        rows={Math.min(10, Math.max(3, Math.ceil(value.length / 60)))}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input type="text" className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />
    )
  }

  if (typeof value === 'number') {
    return (
      <input
        type="number"
        className={inputClass}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    )
  }

  if (typeof value === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
        />
        {value ? 'Yes' : 'No'}
      </label>
    )
  }

  if (Array.isArray(value)) {
    return (
      <div className="space-y-3">
        {value.map((item, i) => (
          <div key={i} className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {label ? `${label} ` : ''}#{i + 1}
              </span>
              <button
                type="button"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
            <JsonForm
              value={item}
              depth={depth + 1}
              onChange={(next) => {
                const copy = value.slice()
                copy[i] = next
                onChange(copy)
              }}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...value, cloneAsTemplate(value[value.length - 1])])}
          disabled={value.length === 0}
          className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> Add {label ? label.toLowerCase() : 'item'}
        </button>
      </div>
    )
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    return (
      <div className={depth === 0 ? 'space-y-5' : 'space-y-4'}>
        {entries.map(([key, v]) => {
          const isSimple = typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
          const fieldLabel = humanizeKey(key)
          // Top-level and simple-value fields render inline with a label.
          // Nested objects/arrays get a collapsible section so a 14-field
          // page doesn't turn into one enormous unreadable form.
          if (isSimple || depth > 0) {
            return (
              <div key={key}>
                <label className="mb-1 block text-sm font-medium text-gray-700">{fieldLabel}</label>
                <JsonForm
                  value={v}
                  label={fieldLabel}
                  depth={depth + 1}
                  onChange={(next) => onChange({ ...(value as object), [key]: next })}
                />
              </div>
            )
          }
          return (
            <details key={key} open={depth === 0} className="rounded-lg border border-gray-200 bg-white">
              <summary className="flex cursor-pointer select-none items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900">
                {fieldLabel}
                <ChevronDown className="h-4 w-4 text-gray-400 transition-transform [details[open]_&]:rotate-180" />
              </summary>
              <div className="border-t border-gray-100 p-4">
                <JsonForm
                  value={v}
                  label={fieldLabel}
                  depth={depth + 1}
                  onChange={(next) => onChange({ ...(value as object), [key]: next })}
                />
              </div>
            </details>
          )
        })}
      </div>
    )
  }

  // null / undefined — shouldn't occur in our static content, but render
  // something editable rather than silently dropping the field.
  return (
    <input
      type="text"
      className={inputClass}
      value=""
      placeholder="(empty)"
      onChange={(e) => onChange(e.target.value)}
    />
  )
}
